import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Star, ShieldCheck, Heart, Briefcase, Music, Users, Flag, Handshake, Lock, Car, ArrowUpRight,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { SERVICES, BRAND } from "../data/content";
import { Reveal } from "./Reveal";
import { track } from "../lib/tracking";

const ICONS = {
  star: Star, shield: ShieldCheck, heart: Heart, briefcase: Briefcase, music: Music,
  users: Users, flag: Flag, handshake: Handshake, lock: Lock, car: Car,
};

export const ServicesGrid = ({ limit }) => {
  const [active, setActive] = useState(null);
  const list = limit ? SERVICES.slice(0, limit) : SERVICES;

  return (
    <>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="services-grid">
        {list.map((s, i) => {
          const Icon = ICONS[s.icon] || ShieldCheck;
          return (
            <Reveal key={s.id} delay={(i % 3) * 0.08}>
              <button
                data-testid={`service-card-${s.id}`}
                onClick={() => {
                  setActive(s);
                  track("service_view", { service: s.id });
                }}
                className="group relative flex h-full w-full flex-col border border-white/[0.07] bg-navy/60 p-7 text-left transition-all duration-500 hover:border-gold/40 hover:bg-navy"
              >
                <Icon size={22} className="text-gold transition-transform duration-500 group-hover:-translate-y-1" />
                <span className="mt-2 font-serif text-[11px] tracking-[0.3em] text-steel/50">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-3 font-serif text-xl font-medium leading-snug text-ivory transition-colors duration-300 group-hover:text-gold sm:text-2xl">
                  {s.title}
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-steel">{s.short}</p>
                <span className="mt-5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold/80 transition-colors duration-300 group-hover:text-gold">
                  View Details
                  <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </button>
            </Reveal>
          );
        })}
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent
          data-testid="service-detail-modal"
          className="max-h-[88vh] overflow-y-auto border-gold/25 bg-obsidian text-ivory sm:max-w-2xl"
        >
          {active && (
            <>
              <DialogHeader>
                <p className="eyebrow mb-2">Service Detail</p>
                <DialogTitle className="font-serif text-2xl font-medium text-ivory sm:text-3xl">
                  {active.title}
                </DialogTitle>
              </DialogHeader>
              <p className="mt-2 text-sm leading-relaxed text-steel">{active.short}</p>

              <h4 className="mt-6 text-[11px] font-semibold uppercase tracking-[0.25em] text-gold">Suitable For</h4>
              <div className="mt-3 flex flex-wrap gap-2">
                {active.suitable.map((t) => (
                  <span key={t} className="border border-gold/25 px-3 py-1.5 text-xs text-ivory/80">{t}</span>
                ))}
              </div>

              <h4 className="mt-6 text-[11px] font-semibold uppercase tracking-[0.25em] text-gold">Scope of Support</h4>
              <p className="mt-2 text-sm leading-relaxed text-steel">{active.scope}</p>

              <h4 className="mt-6 text-[11px] font-semibold uppercase tracking-[0.25em] text-gold">Planning Process</h4>
              <ol className="mt-3 space-y-2">
                {active.planning.map((p, i) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-steel">
                    <span className="font-serif text-gold">{String(i + 1).padStart(2, "0")}</span>
                    {p}
                  </li>
                ))}
              </ol>

              <h4 className="mt-6 text-[11px] font-semibold uppercase tracking-[0.25em] text-gold">Customisation</h4>
              <p className="mt-2 text-sm leading-relaxed text-steel">{active.customisation}</p>

              <div className="mt-8 flex flex-wrap gap-3 border-t border-white/10 pt-6">
                <Link
                  to={`/contact?service=${encodeURIComponent(active.title)}`}
                  data-testid="service-modal-enquire-btn"
                  onClick={() => setActive(null)}
                  className="bg-gold px-6 py-3 text-[12px] font-bold uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-gold-soft"
                >
                  Enquire About This Service
                </Link>
                <a
                  href={BRAND.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="service-modal-whatsapp-btn"
                  onClick={() => track("whatsapp_click", { placement: "service_modal", service: active.id })}
                  className="border border-ivory/25 px-6 py-3 text-[12px] font-bold uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:border-gold hover:text-gold"
                >
                  WhatsApp
                </a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
